import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  PlusIcon, PencilIcon, TrashIcon, EyeIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { fetchArticles, deleteArticle, bulkDeleteArticles } from '../../store/slices/articleSlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Table, Button, SearchInput, Pagination, ConfirmDialog, Card, DynamicBadge } from '../../components/Common';
import { useOptions } from '../../hooks/useOptions';
import api from '../../services/api';

const ArticleList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { articles, loading, pagination } = useSelector((state) => state.article);
  
  // Use dynamic options (initialized for future use)
  useOptions('articleStatus');
  useOptions('articleLabels');
  
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('created_on');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  
  // Filter states
  const [authorFilter, setAuthorFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dropdown data
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Author suggestions for autocomplete
  const [showAuthorSuggestions, setShowAuthorSuggestions] = useState(false);
  const [authorSuggestions, setAuthorSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Handle search change - reset author filter when searching
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    
    // Show author suggestions when typing 2+ characters
    if (value.trim().length >= 2) {
      const searchLower = value.toLowerCase();
      const filtered = authors.filter(author => 
        author.name.toLowerCase().includes(searchLower)
      );
      setAuthorSuggestions(filtered);
      setShowAuthorSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowAuthorSuggestions(false);
      setAuthorSuggestions([]);
      setSelectedSuggestionIndex(-1);
    }
    
    if (value.trim().length > 0) {
      setAuthorFilter('all'); // Reset author filter when searching
    }
    setPage(1);
  }, [authors]);
  
  // Handle keyboard navigation in suggestions
  const handleSearchKeyDown = (e) => {
    if (!showAuthorSuggestions || authorSuggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < authorSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleAuthorSuggestionClick(authorSuggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowAuthorSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };
  
  // Handle author suggestion click
  const handleAuthorSuggestionClick = (author) => {
    setSearch(author.name); // Set search to author name
    setShowAuthorSuggestions(false); // Hide suggestions
    setAuthorSuggestions([]);
    setSelectedSuggestionIndex(-1);
    setPage(1);
  };

  // Handle author filter change - reset search when using author filter
  const handleAuthorFilterChange = (value) => {
    setAuthorFilter(value);
    if (value !== 'all') {
      setSearch(''); // Reset search when using author filter
      setShowAuthorSuggestions(false);
    }
    setPage(1);
  };

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch authors from the articles/authors endpoint
        const authorsResponse = await api.get('/articles/authors');
        if (authorsResponse.data.success) {
          const authorsArray = authorsResponse.data.data || [];
          setAuthors(authorsArray);
        }
        
        // Fetch categories
        const categoriesResponse = await api.get('/categories/dropdown');
        if (categoriesResponse.data.success) {
          const categoriesArray = categoriesResponse.data.data || [];
          setCategories(categoriesArray);
        }
      } catch (error) {
        console.error('Failed to fetch dropdown data:', error);
        setAuthors([]);
        setCategories([]);
      }
    };
    
    fetchDropdownData();
  }, []);

  useEffect(() => {
    dispatch(fetchArticles({ 
      page, 
      size: pageSize, 
      search, 
      sortBy, 
      sortOrder,
      author: authorFilter,
      category: categoryFilter,
      status: statusFilter
    }));
  }, [dispatch, page, pageSize, search, sortBy, sortOrder, authorFilter, categoryFilter, statusFilter]);

  const handleSort = (key, order) => {
    setSortBy(key);
    setSortOrder(order);
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteArticle(deleteModal.id)).unwrap();
        dispatch(showSuccess('Article deleted successfully'));
        dispatch(fetchArticles({ 
          page, 
          size: pageSize, 
          search, 
          sortBy, 
          sortOrder,
          author: authorFilter,
          category: categoryFilter,
          status: statusFilter
        }));
      } catch (error) {
        dispatch(showError(error || 'Failed to delete article'));
      }
      setDeleteModal({ open: false, id: null });
    }
  };

  const handleBulkDelete = async () => {
    try {
      await dispatch(bulkDeleteArticles(selectedRows)).unwrap();
      dispatch(showSuccess(`${selectedRows.length} articles deleted successfully`));
      setSelectedRows([]);
      dispatch(fetchArticles({ 
        page, 
        size: pageSize, 
        search, 
        sortBy, 
        sortOrder,
        author: authorFilter,
        category: categoryFilter,
        status: statusFilter
      }));
    } catch (error) {
      dispatch(showError(error || 'Failed to delete articles'));
    }
    setBulkDeleteModal(false);
  };

  const handleSelectRow = (id, checked) => {
    setSelectedRows(prev => checked ? [...prev, id] : prev.filter(r => r !== id));
  };

  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? articles.map(a => a.id) : []);
  };

  // Remove static badge functions - use DynamicBadge instead

  const columns = [
    {
      key: 'title',
      title: 'Title',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          {row.coverImage?.url ? (
            <img src={row.coverImage.url} alt="" className="w-12 h-12 rounded-lg object-cover" />
          ) : (
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6 text-slate-400" />
            </div>
          )}
          <div>
            <p className="font-medium text-slate-800 line-clamp-1">{value || 'Untitled'}</p>
            <p className="text-sm text-slate-500 line-clamp-1">{row.brief || 'No description'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'writerName',
      title: 'Author',
      sortable: false,
      render: (value, row) => {
        // Safely handle assignedAuthors
        const assignedAuthors = Array.isArray(row.assignedAuthors) ? row.assignedAuthors : [];
        const authorName = value || 'Unknown';
        const authorInitial = authorName.charAt(0).toUpperCase();
        
        return (
          <div className="flex items-center gap-2">
            {/* Author Avatar */}
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-blue-600">
                {authorInitial}
              </span>
            </div>
            
            {/* Author Name */}
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-slate-700 truncate" title={authorName}>
                {authorName}
              </span>
              
              {/* Multiple Authors Indicator */}
              {assignedAuthors.length > 1 && (
                <span 
                  className="text-xs text-blue-600 cursor-help hover:text-blue-700"
                  title={assignedAuthors.map(a => a?.name || 'Unknown').join(', ')}
                >
                  +{assignedAuthors.length - 1} more
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'category',
      title: 'Category',
      render: (value) => (
        <span className="text-slate-600">{value?.name || 'Uncategorized'}</span>
      ),
    },
    {
      key: 'label',
      title: 'Label',
      render: (value) => value?.id ? <DynamicBadge optionType="articleLabels" value={value.id} /> : '-',
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => <DynamicBadge optionType="articleStatus" value={value?.id || value} dot />,
    },
    {
      key: 'totalViews',
      title: 'Views',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1">
          <EyeIcon className="w-4 h-4 text-slate-400" />
          <span className="text-slate-700 font-medium">{value || 0}</span>
        </div>
      ),
    },
    {
      key: 'totalLikes',
      title: 'Likes',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1">
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <span className="text-slate-700 font-medium">{value || 0}</span>
        </div>
      ),
    },
    {
      key: 'created_on',
      title: 'Created',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/articles/view/${row.id}`)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="View"
          >
            <EyeIcon className="w-4 h-4 text-slate-500" />
          </button>
          <button
            onClick={() => navigate(`/articles/edit/${row.id}`)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="Edit"
          >
            <PencilIcon className="w-4 h-4 text-slate-500" />
          </button>
          <button
            onClick={() => setDeleteModal({ open: true, id: row.id })}
            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Articles</h1>
          <p className="text-slate-500">Manage your articles and content</p>
        </div>
        <Link to="/articles/new">
          <Button icon={PlusIcon}>New Article</Button>
        </Link>
      </div>

      {/* Filters */}
      <Card noPadding>
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-col gap-4">
            {/* Search and Bulk Actions Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <SearchInput
                  value={search}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => {
                    // Show suggestions if there's a search term
                    if (search.trim().length >= 2 && authorSuggestions.length > 0) {
                      setShowAuthorSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    // Delay hiding to allow click on suggestion
                    setTimeout(() => {
                      setShowAuthorSuggestions(false);
                      setSelectedSuggestionIndex(-1);
                    }, 200);
                  }}
                  placeholder="Search articles by title, description, or author name..."
                  className="flex-1"
                />
                
                {/* Author Suggestions Dropdown */}
                {showAuthorSuggestions && authorSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-2 bg-slate-50 border-b border-slate-200">
                      <p className="text-xs font-medium text-slate-600">
                        👤 Author Suggestions ({authorSuggestions.length})
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Use ↑↓ arrows to navigate, Enter to select
                      </p>
                    </div>
                    {authorSuggestions.map((author, index) => (
                      <button
                        key={author.id}
                        type="button"
                        onClick={() => handleAuthorSuggestionClick(author)}
                        className={`w-full px-4 py-2 text-left flex items-center gap-3 transition-colors border-b border-slate-100 last:border-b-0 ${
                          index === selectedSuggestionIndex 
                            ? 'bg-blue-100 border-blue-200' 
                            : 'hover:bg-blue-50'
                        }`}
                      >
                        {/* Author Avatar */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          index === selectedSuggestionIndex ? 'bg-blue-600' : 'bg-blue-100'
                        }`}>
                          <span className={`text-sm font-semibold ${
                            index === selectedSuggestionIndex ? 'text-white' : 'text-blue-600'
                          }`}>
                            {author.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        
                        {/* Author Info */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            index === selectedSuggestionIndex ? 'text-blue-900' : 'text-slate-800'
                          }`}>
                            {author.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            Click to view articles
                          </p>
                        </div>
                        
                        {/* Arrow Icon */}
                        <svg className={`w-4 h-4 ${
                          index === selectedSuggestionIndex ? 'text-blue-600' : 'text-slate-400'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedRows.length > 0 && (
                <Button
                  variant="danger"
                  icon={TrashIcon}
                  onClick={() => setBulkDeleteModal(true)}
                >
                  Delete ({selectedRows.length})
                </Button>
              )}
            </div>
            
            {/* Filter Dropdowns Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Author Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Author
                </label>
                <select
                  value={authorFilter}
                  onChange={(e) => handleAuthorFilterChange(e.target.value)}
                  disabled={search.trim().length > 0}
                  className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    search.trim().length > 0 ? 'bg-slate-100 cursor-not-allowed opacity-60' : ''
                  }`}
                  title={search.trim().length > 0 ? 'Author filter is disabled when searching' : ''}
                >
                  <option value="all">All Authors</option>
                  {Array.isArray(authors) && authors.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </select>
                {search.trim().length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    💡 Clear search to use author filter
                  </p>
                )}
              </div>
              
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category
                </label>
                <select
                  value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="all">All Categories</option>
                  {Array.isArray(categories) && categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="1">Published</option>
                  <option value="3">Draft</option>
                  <option value="4">Scheduled</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={articles}
          loading={loading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          selectable
          selectedRows={selectedRows}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          emptyMessage="No articles found"
          emptyIcon={DocumentTextIcon}
        />

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </Card>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onConfirm={handleDelete}
        title="Delete Article"
        message="Are you sure you want to delete this article? This action cannot be undone."
        type="danger"
        confirmText="Delete"
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        isOpen={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Delete Multiple Articles"
        message={`Are you sure you want to delete ${selectedRows.length} articles? This action cannot be undone.`}
        type="danger"
        confirmText="Delete All"
      />
    </div>
  );
};

export default ArticleList;
